import FormDataPolyfill from "formdata-polyfill";
import {makeApiUrl} from "../util/config.jsx";

const StatusCodes = {
    SUCCESS: 1,
    FAILED: 2
};

const ErrorTypes = {
    NETWORK_ERROR: "NETWORK_ERROR",
    REMOTE_ERROR: "REMOTE_ERROR",
    USER_ERROR: "USER_ERROR",
    UNAUTHORIZED: "UNAUTHORIZED",
    ACCESS_DENIED: "ACCESS_DENIED",
    LOCAL_ERROR: "LOCAL_ERROR"
};

export const ContentTypes = {
    JSON: "application/json",
    IMAGE: "image/jpeg,image/png"
};

export class NetworkError extends Error {
    /**
     * @param {string} url
     * @param {string} errorType
     * @param {any} payload
     * @param {string?} message
     * @param {number?} userExceptionType
     */
    constructor(url, errorType, payload, message, userExceptionType) {
        super(message);
        this.url = url;
        this.errorType = errorType;
        this.payload = payload;
        this.message = message;
        this.userExceptionType = userExceptionType;
    }

    /**
     * @return {{NETWORK_ERROR: string, REMOTE_ERROR: string, USER_ERROR: string, UNAUTHORIZED: string, ACCESS_DENIED: string, LOCAL_ERROR: string}}
     * @constructor
     */
    static get ErrorType() {
        return ErrorTypes;
    }
}

/**
 * Perform a HTTP POST with the body as the specified content type and return the response parsed as the accept content type.
 * @param {string} url - The url to post to.
 * @param {Object} data - Payload which will be serialized to JSON.
 * @param {string?} token - A JWT if the user is logged in.
 * @param {string} contentType - We can ask for json or images
 * @param {string} acceptType - We can ask for json or images
 * @return {{payload: Object?, message: string?}}
 * @throws {NetworkError}
 */
export async function makePostRequest(url, data, token, contentType = ContentTypes.JSON, acceptType = ContentTypes.JSON) {
    const headers = new Headers();
    headers.append("Content-Type", contentType);
    headers.append("Accept", acceptType);
    if (token) {
        headers.append("Authorization", `Bearer ${token}`);
    }

    let httpResponse;
    try {
        httpResponse = await fetch(makeApiUrl(url), {method: "POST", headers, mode: "cors", body: JSON.stringify(data)});
    } catch (error) {
        throw new NetworkError(makeApiUrl(url), ErrorTypes.NETWORK_ERROR, error, null);
    }

    if (!httpResponse.ok) {
        let parsedError;
        try {
            parsedError = await httpResponse.json();
        } catch (error) {
            parsedError = undefined;
        }

        if (httpResponse.status === 401) {
            throw new NetworkError(makeApiUrl(url), ErrorTypes.UNAUTHORIZED, undefined, parsedError && parsedError.message);
        } else if (httpResponse.status === 403) {
            throw new NetworkError(makeApiUrl(url), ErrorTypes.ACCESS_DENIED, undefined, parsedError && parsedError.message);
        } else if (httpResponse.status >= 400 && httpResponse.status < 500) {
            throw new NetworkError(makeApiUrl(url), ErrorTypes.USER_ERROR, parsedError && parsedError.payload, parsedError && parsedError.message, parsedError && parsedError.userExceptionType);
        } else if ([502, 504].some(code => code === httpResponse.status)){
            throw new NetworkError(makeApiUrl(url), ErrorTypes.NETWORK_ERROR, undefined, "Gateway Timeout");
        } else {
            throw new NetworkError(makeApiUrl(url), ErrorTypes.REMOTE_ERROR, parsedError && parsedError.payload, parsedError && parsedError.message);
        }
    }

    let parsedResponse;
    try {
        if(acceptType == ContentTypes.IMAGE)
            return httpResponse;
        parsedResponse = await httpResponse.json();
    } catch (error) {
        throw new NetworkError(makeApiUrl(url), ErrorTypes.NETWORK_ERROR, error, null);
    }

    if (parsedResponse.status === StatusCodes.SUCCESS) {
        return {
            payload: parsedResponse.payload,
            message: parsedResponse.message,
        };
    } else {
        throw new NetworkError(makeApiUrl(url), parsedResponse.userExceptionType ? ErrorTypes.USER_ERROR : ErrorTypes.REMOTE_ERROR, parsedResponse.payload, parsedResponse.message, parsedResponse.userExceptionType);
    }
}

/**
 * Perform a HTTP POST with the body as form data and return the response parsed as the accept type.
 * @param {string} url - The url to post to.
 * @param {Array<{name: string, attachment: any}>} attachments - Array of attachments which will be formatted into multipart formdata.
 * @param {string?} token - A JWT if the user is logged in.
 * @param {string} acceptType - We can ask for json or images
 * @return {{payload: Object?, message: string?}}
 * @throws {NetworkError}
 */
export async function postFormdata(url, attachments, token, acceptType = ContentTypes.JSON) {
    const headers = new Headers();
    headers.append("Accept", acceptType);
    if (token) {
        headers.append("Authorization", `Bearer ${token}`);
    }

    let form;
    if (FormData) {
        form = new FormData();
    } else {
        form = new FormDataPolyfill();
    }

    for (const attachment of attachments) {
        form.append(attachment.name, attachment.attachment);
    }

    let httpResponse;
    try {
        httpResponse = await fetch(makeApiUrl(url), {method: "POST", headers, mode: "cors", body: (FormData ? form : form._blob())});
    } catch (error) {
        throw new NetworkError(makeApiUrl(url), ErrorTypes.NETWORK_ERROR, error, null);
    }

    if (!httpResponse.ok) {
        let parsedError;
        try {
            parsedError = await httpResponse.json();
        } catch (error) {
            parsedError = undefined;
        }

        if (httpResponse.status === 401) {
            throw new NetworkError(makeApiUrl(url), ErrorTypes.UNAUTHORIZED, undefined, parsedError && parsedError.message);
        } else if (httpResponse.status === 403) {
            throw new NetworkError(makeApiUrl(url), ErrorTypes.ACCESS_DENIED, undefined, parsedError && parsedError.message);
        } else if (httpResponse.status >= 400 && httpResponse.status < 500) {
            throw new NetworkError(makeApiUrl(url), ErrorTypes.USER_ERROR, parsedError && parsedError.payload, parsedError && parsedError.message, parsedError && parsedError.userExceptionType);
        } else if ([502, 504].some(code => code === httpResponse.status)){
            throw new NetworkError(makeApiUrl(url), ErrorTypes.NETWORK_ERROR, undefined, "Gateway Timeout");
        } else {
            throw new NetworkError(makeApiUrl(url), ErrorTypes.REMOTE_ERROR, parsedError && parsedError.payload, parsedError && parsedError.message);
        }
    }

    let parsedResponse;
    try {
        if(acceptType == ContentTypes.IMAGE)
            return httpResponse;
        parsedResponse = await httpResponse.json();
    } catch (error) {
        throw new NetworkError(makeApiUrl(url), ErrorTypes.NETWORK_ERROR, error, null);
    }

    if (parsedResponse.status === StatusCodes.SUCCESS) {
        return {
            payload: parsedResponse.payload,
            message: parsedResponse.message,
        };
    } else {
        throw new NetworkError(makeApiUrl(url), parsedResponse.userExceptionType ? ErrorTypes.USER_ERROR : ErrorTypes.REMOTE_ERROR, parsedResponse.payload, parsedResponse.message, parsedResponse.userExceptionType);
    }
}

/**
 * Skapar en query parameter sträng för objekt med djup.
 * 
 * @param {any} queryParameters 
 * @param {string[]} parentNames
 * @returns {string}
 */
const createQueryParameters = (queryParameters, parentNames) => {
    const isObject = obj => obj && Object.keys(obj).length > 0 && typeof(obj) !== "string";
    if (Array.isArray(queryParameters)) {
        return Object.entries(queryParameters).map(kv => {
            const newParentNames = [...parentNames];
            newParentNames[newParentNames.length - 1] = `${newParentNames[newParentNames.length - 1]}[${kv[0]}]`;
            return createQueryParameters(kv[1], newParentNames);
        }).join("&");
    } else if (isObject(queryParameters)) {
        return Object.entries(queryParameters).filter(kv => kv[1] !== undefined).map(kv => {
            return createQueryParameters(kv[1], parentNames.concat(kv[0]));
        }).join("&");
    } else {
        return `${parentNames.join(".")}=${encodeURIComponent(queryParameters)}`;
    }
};

/**
 * Perform a HTTP GET without a body and return the parsed json response.
 * @param {string} url - The url to get.
 * @param {Object.<string, *>?} queryParameters - Key/value map of query parameters to append to the url.
 * @param {string?} token - A JWT if the user is logged in.
 * @param {string} contentType - We can ask for json or images
 * @param {string} acceptType - We can ask for json or images
 * @return {{payload: Object?, message: string, extraInfo: *}}
 * @throws {NetworkError}
 */
export async function makeGetRequest(url, queryParameters, token, contentType = ContentTypes.JSON, acceptType = ContentTypes.JSON) {
    const headers = new Headers();
    if (contentType != null) {
        headers.append("Content-Type", contentType);
    }
    headers.append("Accept", acceptType);
    if (token) {
        headers.append("Authorization", `Bearer ${token}`);
    }

    let httpResponse;
    try {
        const queryParameterString = (queryParameters && Object.values(queryParameters).find(p => p !== undefined) !== undefined) ?`?${createQueryParameters(queryParameters, [])}`:"";
        httpResponse = await fetch(`${makeApiUrl(url)}${queryParameterString}`, {method: "GET", headers, mode: "cors"});
    } catch (error) {
        throw new NetworkError(makeApiUrl(url), ErrorTypes.NETWORK_ERROR, error, null);
    }

    if (!httpResponse.ok) {
        let parsedError;
        try {
            parsedError = await httpResponse.json();
        } catch (error) {
            parsedError = undefined;
        }

        if (httpResponse.status === 401) {
            throw new NetworkError(makeApiUrl(url), ErrorTypes.UNAUTHORIZED, undefined, parsedError && parsedError.message);
        } else if (httpResponse.status === 403) {
            throw new NetworkError(makeApiUrl(url), ErrorTypes.ACCESS_DENIED, undefined, parsedError && parsedError.message);
        } else if (httpResponse.status >= 400 && httpResponse.status < 500) {
            throw new NetworkError(makeApiUrl(url), ErrorTypes.USER_ERROR, parsedError && parsedError.payload, parsedError && parsedError.message, parsedError && parsedError.userExceptionType);
        } else if ([502, 504].some(code => code === httpResponse.status)){
            throw new NetworkError(makeApiUrl(url), ErrorTypes.NETWORK_ERROR, undefined, "Gateway Timeout");
        } else {
            throw new NetworkError(makeApiUrl(url), ErrorTypes.REMOTE_ERROR, parsedError && parsedError.payload, parsedError && parsedError.message);
        }
    }

    let parsedResponse;
    try {        
        if(acceptType == ContentTypes.IMAGE)
            return httpResponse;
        parsedResponse = await httpResponse.json();
    } catch (error) {
        throw new NetworkError(makeApiUrl(url), ErrorTypes.NETWORK_ERROR, error, null);
    }

    if (parsedResponse.status === StatusCodes.SUCCESS) {
        return {
            payload: parsedResponse.payload,
            message: parsedResponse.message,
            extraInfo: parsedResponse.extraInfo
        };
    } else {
        throw new NetworkError(makeApiUrl(url), parsedResponse.userExceptionType ? ErrorTypes.USER_ERROR : ErrorTypes.REMOTE_ERROR, parsedResponse.payload, parsedResponse.message, parsedResponse.userExceptionType);
    }
}